import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api/client';
import { Lock, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('Reset token is missing. Please check your email link.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        // Basic complexity check
        const policy = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!policy.test(newPassword)) {
            setError('Password must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
            return;
        }

        setLoading(true);

        try {
            await api.post('/admin/auth/reset-password', { 
                token, 
                newPassword 
            }, {
                skipAuth: true
            } as any);

            setSubmitted(true);
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data || 'Failed to reset password. The link may have expired.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen w-full bg-white">
            {/* Left Side - Form */}
            <div className="flex w-full flex-col justify-center px-8 lg:w-1/2 lg:px-24">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-6">
                        <Link to="/login" className="text-slate-600 hover:text-slate-900 transition-colors">
                            <ArrowLeft className="h-6 w-6" />
                        </Link>
                        <div className="w-10 h-10 bg-blue-900 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">N</span>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">Admin Portal</h1>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">Reset Password</h2>
                    <p className="text-slate-500">Please enter your new password.</p>
                </div>

                {!submitted ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="p-4 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
                                {error}
                            </div>
                        )}

                        {!token && (
                            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100 text-amber-700 text-sm">
                                Warning: No reset token found in URL. This request will likely fail.
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">New Password</label>
                            <div className="relative group">
                                <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                                <input
                                    type="password"
                                    required
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
                            <div className="relative group">
                                <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                                <input
                                    type="password"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-900 hover:bg-blue-800 text-white font-medium py-2.5 rounded-lg transition-all shadow-lg shadow-blue-900/10 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    <span>Resetting...</span>
                                </>
                            ) : (
                                <span>Reset Password</span>
                            )}
                        </button>
                    </form>
                ) : (
                    <div className="text-center p-8 bg-green-50 rounded-xl border border-green-100">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">Password Reset Successful</h3>
                        <p className="text-sm text-slate-600 mb-6">
                            Your password has been reset successfully. You can now sign in with your new password.
                        </p>
                        <Link
                            to="/login"
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                            Back to Sign In
                        </Link>
                    </div>
                )}

                <div className="mt-8 text-center">
                    <p className="text-xs text-slate-400">
                        © 2026 Xtrem Systems. All rights reserved.
                    </p>
                </div>
            </div>

            {/* Right Side - Hero Gradient */}
            <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-overlay filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>

                <div className="relative z-10 p-12 text-white max-w-lg">
                    <div className="mb-8">
                        <div className="h-1 w-20 bg-blue-500 rounded-full mb-6"></div>
                        <h2 className="text-4xl font-bold mb-6 leading-tight tracking-tight">
                            Enterprise Administration <br />
                            <span className="text-blue-400">Reimagined.</span>
                        </h2>
                        <p className="text-blue-100 text-lg leading-relaxed">
                            Securely manage policies, claims, and customers with our unified Xtrem dashboard.
                            Designed for efficiency, built for scale.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
